import { useState, useEffect, useCallback } from 'react';

type ProviderStatus = 'green' | 'yellow' | 'red' | 'gray';

interface ProviderResult {
  name: string;
  status: ProviderStatus;
  ok: boolean;
  latencyMs: number;
  lastCheckedAt: string;
  message: string;
  meta: Record<string, unknown>;
}

interface HealthResponse {
  ok: boolean;
  providers: ProviderResult[];
  checkedAt: string;
}

const REFRESH_MS = 30_000;
const TOKEN_KEY = 'ceo_provider_health_token';

const STATUS_LABELS: Record<ProviderStatus, string> = {
  green: 'Healthy',
  yellow: 'Slow',
  red: 'Down',
  gray: 'Unknown',
};

function relativeTime(iso: string): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 1000) return 'Just now';
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

function formatLatency(ms: number): string {
  if (ms === 0) return '--';
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

export function ProviderHealth() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) ?? '');
  const [tokenInput, setTokenInput] = useState('');
  const [data, setData] = useState<HealthResponse | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchHealth = useCallback(async (t: string) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/admin/ceo/provider-health', {
        headers: { 'x-ceo-token': t },
      });
      if (!res.ok) {
        if (res.status === 403) {
          setError('Invalid CEO token');
          sessionStorage.removeItem(TOKEN_KEY);
          setToken('');
        } else {
          setError(`API error: ${res.status}`);
        }
        setData(null);
        return;
      }
      const json = (await res.json()) as HealthResponse;
      setData(json);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fetch failed');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!tokenInput.trim()) return;
    sessionStorage.setItem(TOKEN_KEY, tokenInput.trim());
    setToken(tokenInput.trim());
    fetchHealth(tokenInput.trim());
  };

  useEffect(() => {
    if (!token) return;
    fetchHealth(token);
    const id = setInterval(() => fetchHealth(token), REFRESH_MS);
    return () => clearInterval(id);
  }, [token, fetchHealth]);

  if (!token) {
    return (
      <div className="ph-page">
        <div className="ph-login-card">
          <h1>Provider Health Monitor</h1>
          <p className="ph-login-desc">Enter your CEO dashboard token to view provider status.</p>
          <form onSubmit={handleLogin} className="ph-login-form">
            <input
              type="password"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder="CEO Dashboard Token"
              className="ph-login-input"
              autoFocus
            />
            <button type="submit" className="btn btn-primary">
              Authenticate
            </button>
          </form>
          {error && <p className="ph-error">{error}</p>}
        </div>
      </div>
    );
  }

  const degradedCount = data ? data.providers.filter((p) => !p.ok).length : 0;

  return (
    <div className="ph-page">
      <div className="ph-header">
        <h1>Provider Health Monitor</h1>
        {data && (
          <div className={`ph-banner ph-banner-${data.ok ? 'ok' : 'degraded'}`}>
            {data.ok
              ? 'All Systems Operational'
              : `${degradedCount} provider${degradedCount > 1 ? 's' : ''} degraded`}
          </div>
        )}
        {loading && !data && <p className="ph-loading">Loading...</p>}
        {error && <p className="ph-error">{error}</p>}
      </div>

      {data && (
        <div className="ph-grid">
          {data.providers.map((p) => (
            <div key={p.name} className={`ph-card ph-card-${p.status}`}>
              <div className="ph-card-header">
                <span className={`ph-dot ph-dot-${p.status}`} />
                <span className="ph-card-name">{p.name}</span>
                <span className="ph-card-status-label">{STATUS_LABELS[p.status]}</span>
              </div>
              <div className="ph-card-body">
                <div className="ph-metric">
                  <span className="ph-metric-label">Latency</span>
                  <span className="ph-metric-value">{formatLatency(p.latencyMs)}</span>
                </div>
                <div className="ph-metric">
                  <span className="ph-metric-label">Last Checked</span>
                  <span className="ph-metric-value">{relativeTime(p.lastCheckedAt)}</span>
                </div>
              </div>
              <p className="ph-card-message">{p.message}</p>
              {Object.keys(p.meta).length > 0 && (
                <div className="ph-card-meta">
                  {Object.entries(p.meta).map(([k, v]) => (
                    <span key={k} className="ph-meta-tag">
                      {k}: {String(v)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {data && (
        <p className="ph-footer">
          Auto-refreshes every 30s &middot; Last fetch: {relativeTime(data.checkedAt)}
        </p>
      )}
    </div>
  );
}
